import React, { useState, useRef, useReducer, useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { motion } from 'framer-motion'
import { useDebouncedEffect } from '@/utils/useDebouncedEffect'
import { MobileAdBundle, SidebarAdBundle, InFeedAd } from '@/Layout/Ads'
import { requestEzoicAds } from '@/utils/ezoicAds'
import { useGlobalState } from '@/state'
import classNames from '@/utils/classNamesLocal'
import useDropdown from '@/hooks/useDropdown'
import SavedSearchProjectModal from '@/shared/SavedSearchProjectModal'
import Loading from '@/shared/Loading'
import useQuery from '@/hooks/useQuery'
import SVGIcon from '@/components/icon'
import TagAutocomplete from '@/shared/TagAutocomplete'
import {
  XIcon, CogIcon, ChevronUpIcon, ChevronDownIcon, GlobeAltIcon, MicrophoneIcon,
  SearchIcon, AdjustmentsIcon, SortAscendingIcon, ViewGridIcon, StarIcon, ClockIcon, CheckIcon
} from '@heroicons/react/solid'

// In-feed ad cadence. First slot only after the 4th result so visitors reach
// real results before an ad, then one every 8. Ezoic's AI skips these
// (`required: false`) whenever page density is already high, so this offers
// opportunities rather than forcing a count — tightening much below ~6 mostly
// adds slots Ezoic declines rather than revenue. Tune here.
const IN_FEED_FIRST_AFTER = 4
const IN_FEED_EVERY = 8

const talentKindHash = {
  'voice_actor': { image: 'voiceactor', text: 'Voice Actor', to_db: 'voice_actor' },
  'live_actor': { image: 'actor', text: 'Actor', to_db: 'live_actor' },
  'singer': { image: 'singer', text: 'Singer', to_db: 'singer' },
  'artist': { image: 'artist', text: 'Artist', to_db: 'artist' },
  'animator': { image: 'animator', text: 'Animator', to_db: 'animator' },
  'video_editor': { image: 'video-editor', text: 'Video Editor', to_db: 'video_editor' },
  'music_composer': { image: 'musiccomposer', text: 'Musician', to_db: 'music_composer' },
  'audio_engineer': { image: 'audio-engineer', text: 'Audio Eng', to_db: 'audio_engineer' },
  'writer': { image: 'writer', text: 'Writer', to_db: 'writer' },
  'director': { image: 'director', text: 'Director', to_db: 'director' },
  'agent': { image: 'agent', text: 'Agent', to_db: 'agent' },
  'producer': { image: 'producer', text: 'Producer', to_db: 'producer' }
}
// Tucked behind "More" so the kind row stays one line on desktop.
const RARE_KINDS = ['live_actor', 'director', 'agent', 'producer']

// The API rotates each project's `bgColor` through these three. Literal class
// strings so Tailwind's purge keeps them; template strings like
// `bg-${bgColor}` only work when the class happens to exist somewhere else.
const ACCENT = {
  cccpurple: { stripe: 'bg-cccpurple', text: 'text-cccpurple', ring: 'ring-cccpurple', border: 'border-cccpurple', soft: 'bg-cccpurple/10 text-cccpurple dark:bg-cccpurple/20 dark:text-white' },
  cccblue: { stripe: 'bg-cccblue', text: 'text-cccblue', ring: 'ring-cccblue', border: 'border-cccblue', soft: 'bg-cccblue/10 text-cccblue dark:bg-cccblue/20 dark:text-white' },
  cccorange: { stripe: 'bg-cccorange', text: 'text-cccorange', ring: 'ring-cccorange', border: 'border-cccorange', soft: 'bg-cccorange/10 text-cccorange dark:bg-cccorange/20 dark:text-white' }
}
const accentFor = (bgColor) => ACCENT[bgColor] || ACCENT.cccblue

// Every label-backed filter, grouped the way people think about them. Each
// entry drives the filter panel, the active-chip row, add/remove, and the URL
// round-trip below, so adding a kind is one line here.
const FILTER_GROUPS = [
  {
    title: 'Voice',
    icon: MicrophoneIcon,
    kinds: [
      { key: 'genderAges', kind: 'user-gender-age', label: 'Voice age & gender', placeholder: 'Female young adult, male teen, baby', noun: 'voice ages and genders' },
      { key: 'demoCategories', kind: 'demo-categories', label: 'Voice description', placeholder: 'Warm, raspy, suspiciously evil', noun: 'voice descriptions' },
      { key: 'accents', kind: 'user-accents', label: 'Accents', placeholder: 'Texan, Irish, French', noun: 'accents' },
      { key: 'languages', kind: 'user-languages', label: 'Language spoken', placeholder: 'English, Japanese', noun: 'languages' }
    ]
  },
  {
    title: 'Setup & skills',
    icon: CogIcon,
    kinds: [
      { key: 'skills', kind: 'user-skills', label: 'Skills', placeholder: 'Kung fu fighting, foreboding glare, mediocre dad jokes', noun: 'skills' },
      { key: 'softwares', kind: 'user-softwares', label: 'Software', placeholder: 'Audacity, Source Connect, Scrivener', noun: 'software' },
      { key: 'hardwares', kind: 'user-hardwares', label: 'Hardware', placeholder: 'Scarlett 2i2, Shure SM7b, Blue Yeti', noun: 'hardware' },
      { key: 'interests', kind: 'user-interests', label: 'Interests', placeholder: 'Manga, music, audiobooks', noun: 'interests' }
    ]
  }
]
const FILTER_KINDS = FILTER_GROUPS.flatMap(g => g.kinds)
const KIND_TO_KEY = Object.fromEntries(FILTER_KINDS.map(k => [k.kind, k.key]))

const PAYMENT_OPTIONS = [['all', 'Any pay'], ['paid', 'Paid'], ['deferred', 'Deferred'], ['unpaid', 'Unpaid']]
const CLASSIFICATION_OPTIONS = [['all', 'Any type'], ['Original', 'Original'], ['Fan', 'Fan']]

const orderMap = {
  magic: 'Literal Magic',
  popular: 'Popular',
  deadline: 'Deadline',
  updated_at: 'Latest Activity',
  listed_at: 'New'
}

const cleanFilters = () => ({
  talentKind: 'all',
  contains: [],
  notContains: [],
  containsMode: 'and',
  ...Object.fromEntries(FILTER_KINDS.flatMap(k => [[k.key, []], [`${k.key}Mode`, 'and']]))
})

// Map each `<prefix>-mode:or` token in the URL back into the matching React
// state key. Missing tokens fall back to the cleanFilters default of 'and'.
const MODE_TOKEN_TO_STATE_KEY = {
  'co-mode:or': 'containsMode',
  ...Object.fromEntries(FILTER_KINDS.map(k => [`${k.kind}-mode:or`, `${k.key}Mode`]))
}

const arrayifyParams = (params, filters) => {
  if (!params) { return filters }

  const pushUnique = (key, value) => { if (!filters[key].includes(value)) { filters[key].push(value) } }
  for (const item of params.split('|')) {
    if (item === 'is:all') { continue }
    if (MODE_TOKEN_TO_STATE_KEY[item]) { filters[MODE_TOKEN_TO_STATE_KEY[item]] = 'or'; continue }
    const sep = item.indexOf(':')
    if (sep === -1) { continue }
    const prefix = item.slice(0, sep)
    const value = item.slice(sep + 1)
    if (prefix === 'is') { filters.talentKind = value }
    else if (prefix === 'co') { pushUnique('contains', value) }
    else if (prefix === 'nc') { pushUnique('notContains', value) }
    else if (KIND_TO_KEY[prefix]) { pushUnique(KIND_TO_KEY[prefix], value) }
  }

  return filters
}

function useOnScreen(ref) {
  const [isIntersecting, setIntersecting] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    // Create the observer ONCE inside the effect. Previously a fresh
    // IntersectionObserver was allocated on every render; paired with the
    // framer-motion scale animation on the results list, the sentinel flipped
    // in/out of view repeatedly and each flip kicked off another infinite-
    // scroll fetch — hammering /roles_v1/_search. One stable observer fixes it.
    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting)
    )
    observer.observe(node)
    return () => { observer.disconnect() }
  }, [ref])

  return isIntersecting
}

/* ---------- small UI pieces ---------- */

const Chip = ({ children, onRemove, active, onClick, className = '', title }) => {
  const Tag = onClick ? 'button' : 'span'
  return <Tag
    type={onClick ? 'button' : undefined}
    onClick={onClick}
    title={title}
    className={classNames(
      'inline-flex flex-shrink-0 items-center gap-x-1 rounded-full px-3 py-1 text-xs font-semibold capitalize whitespace-nowrap transition-colors',
      onClick && 'focus:outline-none focus:ring-2 focus:ring-cccblue focus:ring-offset-1 dark:focus:ring-offset-gray-800',
      active
        ? 'bg-cccblue text-white'
        : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:ring-cccblue hover:text-cccblue dark:bg-gray-700 dark:text-gray-100 dark:ring-gray-600',
      className
    )}
  >
    {children}
    { onRemove && <button type='button' onClick={onRemove} aria-label='Remove' className='-mr-1 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/20'><XIcon className='h-3.5 w-3.5' /></button> }
  </Tag>
}

const Segmented = ({ value, onChange, options, label }) => (
  <div role='group' aria-label={label} className='inline-flex rounded-full bg-gray-100 p-0.5 dark:bg-gray-700'>
    { options.map(([key, text]) => (
      <button key={key} type='button' onClick={() => onChange(key)}
        className={classNames(
          'rounded-full px-3 py-1 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-cccblue',
          value === key ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white'
        )}>
        {text}
      </button>
    ))}
  </div>
)

const AndOrToggle = ({ value, onChange, noun }) => (
  <div className='inline-flex rounded-full bg-gray-100 p-0.5 text-[11px] dark:bg-gray-700' role='group'>
    { [['and', 'All', `Roles that need ALL of these ${noun}`], ['or', 'Any', `Roles that need ANY of these ${noun}`]].map(([key, text, title]) => (
      <button key={key} type='button' onClick={() => onChange(key)} title={title}
        className={classNames('rounded-full px-2 py-0.5 font-semibold', value === key ? 'bg-cccblue text-white' : 'text-gray-600 dark:text-gray-300')}>
        {text}
      </button>
    ))}
  </div>
)

const LabelPill = ({ kind, accent, text, preview }) => {
  const Icon = kind === 'lang' ? GlobeAltIcon : kind === 'voice' ? MicrophoneIcon : CogIcon
  return <span className={classNames(
    'inline-flex flex-shrink-0 items-center gap-x-1 rounded-full px-2 py-0.5 text-[11px] capitalize',
    preview ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' : accent.soft
  )}>
    <Icon className='h-3 w-3' />
    <span>{text}</span>
  </span>
}

const PayPill = ({ role }) => {
  const paid = role.payment !== 'unpaid'
  return <span className={classNames('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize',
    paid ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100' : 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-200')}>
    { role.paymentTotal === null ? role.payment : <>~{role.paymentTotal} total</> }
  </span>
}

// One "+ Kind" pill per filter. Click opens a small popover holding the
// autocomplete; chosen values live in the active-chip row, not here.
const FilterPill = ({ label, count, open, onToggle, onClose, children }) => {
  const ref = useRef()
  useEffect(() => {
    if (!open) { return undefined }
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { onClose() } }
    const onKey = (e) => { if (e.key === 'Escape') { onClose() } }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])

  return <div ref={ref} className='relative'>
    <Chip active={open || count > 0} onClick={onToggle} title={`Filter by ${label.toLowerCase()}`}>
      <span aria-hidden='true'>+</span> {label}
      { count > 0 && <span className='ml-0.5 rounded-full bg-white/90 px-1.5 text-[10px] text-cccblue'>{count}</span> }
    </Chip>
    { open && <div className='absolute left-0 top-full z-30 mt-1 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-600 dark:bg-gray-800'>
      <div className='mb-1 text-xs font-semibold text-gray-600 dark:text-gray-300'>{label}</div>
      {children}
      <div className='mt-1 text-[11px] text-gray-400'>Enter or comma adds it. Pick as many as you like.</div>
    </div> }
  </div>
}

/* ---------- roles ---------- */

const RoleSearchRow = ({ project, role }) => {
  const [expandedRole, setExpandedRole] = useState({})
  const [loading, setLoading] = useState(false)
  const { getRequest } = useQuery()
  const accent = accentFor(project.bgColor)
  const labels = role.searchLabels || {}
  const preview = [
    ...(labels['user-languages'] || []).map(t => ['lang', t]),
    ...(labels['user-accents'] || []).filter(t => t !== 'english').map(t => ['voice', t]),
    ...(labels['user-gender-age'] || []).map(t => ['voice', t]),
    ...(labels['demo-categories'] || []).map(t => ['voice', t]),
    ...(labels['user-skills'] || []).map(t => ['setup', t]),
    ...(labels['user-hardwares'] || []).map(t => ['setup', t]),
    ...(labels['user-softwares'] || []).map(t => ['setup', t])
  ]
  const open = !!expandedRole?.id

  const expand = () => {
    if (open) { setExpandedRole({}); return }
    setLoading(true)
    getRequest('/api/v3/omnisearch/expand_role', { role_id: role.id }, (err, jsonData) => {
      setLoading(false)
      if (err) { /* handled in hook */ return }
      setExpandedRole(jsonData.expandedRole)
    })
  }

  return <div className={classNames('rounded-lg border transition-colors', open ? `bg-gray-50 dark:bg-gray-900/60 ${accent.border}` : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-900/40')}>
    <button type='button' onClick={expand} aria-expanded={open} className='flex w-full items-start gap-x-3 p-2 text-left focus:outline-none focus:ring-2 focus:ring-cccblue rounded-lg'>
      <div className='relative flex-shrink-0'>
        <img className={classNames('h-11 w-11 rounded-lg bg-gray-200 object-cover ring-2', accent.ring)} src={role.imageUrl} alt='' />
        <span className={classNames('absolute -bottom-1 -right-1 rounded-md p-px text-white', accent.stripe)} title={talentKindHash[role.isA]?.text}>
          <SVGIcon name={talentKindHash[role.isA]?.image || 'voiceactor'} className='h-4 w-4' />
        </span>
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
          <span className='font-semibold capitalize text-gray-900 dark:text-white'>{role.name}</span>
          <PayPill role={role} />
        </div>
        { !open && preview.length > 0 && <div className='mt-1 flex flex-wrap gap-1'>
          { preview.slice(0, 8).map(([kind, text], idx) => <LabelPill key={`${role.id}p${idx}`} preview kind={kind} accent={accent} text={text} />) }
          { preview.length > 8 && <span className='text-[11px] text-gray-500 dark:text-gray-400'>+{preview.length - 8} more</span> }
        </div> }
      </div>
      <span className='mt-1 flex-shrink-0 text-gray-400'>
        { open ? <ChevronUpIcon className='h-5 w-5' /> : <ChevronDownIcon className='h-5 w-5' /> }
      </span>
    </button>
    { loading && <div className='px-2 pb-2'><Loading noMessage noLoadingMessage /></div> }
    { open && <div className='space-y-3 px-3 pb-3 text-sm text-gray-800 dark:text-gray-200'>
      <div className='flex flex-wrap gap-1'>
        { expandedRole.languages.map((t, i) => <LabelPill key={`l${i}`} kind='lang' accent={accent} text={t} />) }
        { expandedRole.accents.map((t, i) => <LabelPill key={`a${i}`} kind='voice' accent={accent} text={t} />) }
        { expandedRole.voices.map((t, i) => <LabelPill key={`v${i}`} kind='voice' accent={accent} text={t} />) }
        { expandedRole.skills.map((t, i) => <LabelPill key={`s${i}`} kind='setup' accent={accent} text={t} />) }
        { expandedRole.setups.map((t, i) => <LabelPill key={`u${i}`} kind='setup' accent={accent} text={t} />) }
      </div>
      <div className='prose-sm dark:prose-invert max-w-none' dangerouslySetInnerHTML={{ __html: expandedRole.characteristics }} />
      { expandedRole.line1 && <div className='rounded-lg border-l-4 border-gray-300 bg-white p-3 dark:border-gray-600 dark:bg-gray-800'>
        <div className='mb-1 text-xs font-semibold text-gray-500 dark:text-gray-400'>Audition lines</div>
        <ol className='list-decimal space-y-1 pl-4 italic'>
          {[expandedRole.line1, expandedRole.line2, expandedRole.line3].filter(Boolean).map((line, i) => <li key={i}>{line}</li>)}
        </ol>
      </div> }
    </div> }
  </div>
}

const ProjectRolesIndex = ({ roles, project }) => {
  const [showAll, setShowAll] = useState(false)
  const visible = showAll ? roles : roles.slice(0, 3)
  return <div className='mt-3 space-y-1 border-t border-gray-100 pt-2 dark:border-gray-700'>
    { visible.map((role) => <RoleSearchRow key={`rresult${role.id}`} role={role} project={project} />) }
    { !showAll && roles.length > 3 && <button type='button' onClick={() => setShowAll(true)} className='px-2 py-1 text-sm font-semibold text-cccblue hover:underline'>
      Show {roles.length - 3} more {roles.length - 3 === 1 ? 'role' : 'roles'}
    </button> }
  </div>
}

/* ---------- project card ---------- */

const daysUntil = (iso) => {
  if (!iso) { return null }
  const ms = new Date(iso).getTime() - Date.now()
  // floor, to match the "N days" text beside it (short_time_ago_in_words)
  return Number.isNaN(ms) ? null : Math.floor(ms / 86400000)
}

const IconStat = ({ icon, count, label }) => (
  <span className='inline-flex items-center gap-x-1 text-gray-600 dark:text-gray-300' title={label}>
    <SVGIcon name={icon} className='h-5 w-5' />
    <span className='font-semibold text-gray-900 dark:text-white'>{count}</span>
    <span className='hidden text-xs sm:inline'>{label}</span>
  </span>
)

const IconButton = ({ onClick, label, children, active }) => (
  <button type='button' onClick={onClick} aria-label={label} title={label} aria-pressed={active}
    className={classNames('rounded-full p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-cccblue',
      active ? 'bg-cccorange/10 text-cccorange' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white')}>
    {children}
  </button>
)

const LoadMoreDiv = ({ callback, endOfResults }) => {
  const ref = useRef()
  const isVisible = useOnScreen(ref)
  useEffect(() => { if (isVisible && !endOfResults) { callback() } }, [isVisible])
  return <div ref={ref} className='h-4' aria-hidden='true' />
}

const ProjectResultRow = ({ result, lastElementInList, loadMore, favorites, dismissals, endOfResults }) => {
  const { project, roles } = result
  const { putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const [favorited, setFavorited] = useState(favorites.includes(project.projectId))
  const [dismissed, setDismissed] = useState(dismissals.includes(project.projectId))
  const accent = accentFor(project.bgColor)
  const days = daysUntil(project.deadline)
  const urgent = days !== null && days <= 3

  // `favorited` states what this click MEANS rather than letting the server
  // flip whatever it happens to hold. The old empty body made the server the
  // decider, so any row where this component's seed state was stale turned a
  // click meant as "favorite" into an unfavorite. The response echoes the
  // resulting state; adopt it so a disagreement corrects itself on the spot.
  const handleFavorite = () => {
    const previousState = favorited
    const next = !favorited
    setFavorited(next)
    putpostRequest(`/api/v3/favorites/${project.projectId}/favorite_project`, 'POST', { favorited: next }, (err, jsonData) => {
      if (err) {
        setFavorited(previousState)
        const message = Array.isArray(err) ? err[0] : (typeof err === 'string' ? err : "I couldn't save that favorite. Give it another go?")
        setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
          <p className="text-sm font-medium dark:text-red-100 text-red-600">Well, that didn't work</p>
          <p className="mt-1 text-sm dark:text-red-300 text-red-400">{message}</p>
        </div>)
        return
      }
      if (jsonData && typeof jsonData.favorited === 'boolean') { setFavorited(jsonData.favorited) }
    })
  }

  const handleDismis = () => {
    setDismissed(!dismissed)
    putpostRequest('/api/v3/dismissals/toggle', 'POST', { project_id: project.projectId }, (err, jsonData) => {
      if (err) { return console.log(err) }
    })
  }

  if (dismissed) {
    return <>
      <div className='flex items-center justify-between rounded-xl border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-500 dark:border-gray-600 dark:text-gray-400'>
        <span className='truncate'>Hidden: <a target="_blank" className='capitalize hover:underline' href={`/projects/${project.slug}`} rel="noreferrer">{project.name}</a></span>
        <button type='button' onClick={handleDismis} className='flex-shrink-0 font-semibold text-cccblue hover:underline'>Bring it back</button>
      </div>
      { lastElementInList && <LoadMoreDiv callback={loadMore} endOfResults={endOfResults} /> }
    </>
  }

  return <>
    <article className={classNames('relative overflow-hidden rounded-xl border-l-4 bg-white shadow-sm dark:bg-gray-800', accent.border)}>
      <div className='flex flex-col gap-4 p-4 sm:flex-row'>
        {/* Left: cover + facts */}
        <div className='w-full flex-shrink-0 sm:w-80'>
          <a target="_blank" href={`/projects/${project.slug}`} rel="noreferrer" className='block'>
            <div className='aspect-w-16 aspect-h-9 w-full overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700'>
              <img className='object-cover' src={project.imageUrl} alt={project.name} loading='lazy' />
            </div>
          </a>
          <div className='mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm'>
            <IconStat icon='icon-manage-projects' count={project.rolesCount} label='open roles' />
            <IconStat icon='icon-submissions' count={project.auditionsCount} label='auditions' />
            <IconStat icon='icon-messages' count={project.commentsCount} label='comments' />
            <IconStat icon='icon-followers' count={project.followeesCount} label='following' />
          </div>
        </div>

        {/* Middle: title + description */}
        <div className='min-w-0 flex-1'>
          { project.featured && <span className='mb-1 inline-flex items-center gap-x-1 rounded-full bg-gradient-to-r from-cccpurple via-cccblue to-cccorange px-2 py-0.5 text-[11px] font-bold text-white'>
            <StarIcon className='h-3 w-3' /> Featured
          </span> }
          <h3 className='text-lg font-bold leading-tight'>
            <a target="_blank" href={`/projects/${project.slug}`} rel="noreferrer" className='capitalize text-gray-900 hover:text-cccblue dark:text-white dark:hover:text-cccblue'>{project.name}</a>
          </h3>
          <div className='mt-0.5 text-sm text-gray-500 dark:text-gray-400'>
            by <a className='font-semibold text-gray-700 hover:text-cccblue dark:text-gray-200' href={`/m/${project.username}`}>{project.displayName}</a>
          </div>
          <div className='mt-2 flex flex-wrap items-center gap-1.5 text-xs'>
            <span className={classNames('inline-flex items-center gap-x-1 rounded-full px-2 py-0.5 font-semibold',
              urgent ? 'bg-cccorange text-white' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200')}>
              <ClockIcon className='h-3 w-3' /> Deadline {project.deadlineInWords}
            </span>
            <span className='rounded-full bg-gray-100 px-2 py-0.5 text-gray-700 dark:bg-gray-700 dark:text-gray-200'>Listed {project.listedAtInWords}</span>
            { project.classification && <span className={classNames('rounded-full px-2 py-0.5 font-semibold', accent.soft)}>{project.classification}</span> }
            { project.category && <span className='rounded-full bg-gray-100 px-2 py-0.5 capitalize text-gray-700 dark:bg-gray-700 dark:text-gray-200'>{project.category}</span> }
          </div>
          { project.description && <div className='prose-sm mt-2 line-clamp-6 max-w-none text-sm text-gray-700 dark:text-gray-300' dangerouslySetInnerHTML={{ __html: project.description }} /> }
        </div>

        {/* Right: actions */}
        <div className='flex flex-shrink-0 items-center justify-end gap-x-2 self-start sm:flex-col sm:items-end sm:gap-y-2'>
          <div className='flex items-center gap-x-4'>
            <IconButton onClick={handleFavorite} label={favorited ? 'Unfavorite' : 'Favorite for later'} active={favorited}>
              <SVGIcon name={favorited ? 'icon-favorited' : 'icon-favorites'} className='h-5 w-5' />
            </IconButton>
            <IconButton onClick={handleDismis} label='Hide this project'>
              <SVGIcon name='icon-dismiss' className='h-5 w-5' />
            </IconButton>
          </div>
          <a target="_blank" href={`/projects/${project.slug}`} rel="noreferrer"
            className='inline-flex items-center rounded-full bg-cccblue px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-cccblue-alt hover:text-white focus:outline-none focus:ring-2 focus:ring-cccblue focus:ring-offset-2 dark:focus:ring-offset-gray-800'>
            View project
          </a>
        </div>
      </div>
      <div className='px-2 pb-2 sm:px-4 sm:pb-3'>
        <ProjectRolesIndex roles={roles} project={project} />
      </div>
    </article>
    { lastElementInList && <LoadMoreDiv callback={loadMore} endOfResults={endOfResults} /> }
  </>
}

const EmptyState = ({ clearFilters, undoLast }) => (
  <div className='rounded-xl border-2 border-dashed border-gray-300 p-10 text-center dark:border-gray-600'>
    <div className='text-4xl'>🦗</div>
    <h3 className='mt-3 text-lg font-bold text-gray-900 dark:text-white'>Nothing. Nada. Zilch.</h3>
    <p className='mx-auto mt-1 max-w-sm text-sm text-gray-600 dark:text-gray-300'>I looked everywhere and found zero open roles matching all that. Loosen a filter or two and I'll go look again.</p>
    <div className='mt-4 flex flex-wrap justify-center gap-2'>
      { undoLast && <button type='button' onClick={undoLast} className='inline-flex items-center rounded-full bg-cccblue px-5 py-2 text-sm font-semibold text-white hover:bg-cccblue-alt hover:text-white focus:outline-none focus:ring-2 focus:ring-cccblue focus:ring-offset-2'>
        Remove my last filter
      </button> }
      <button type='button' onClick={clearFilters} className='inline-flex items-center rounded-full bg-gray-100 px-5 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-cccblue dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'>
        Clear everything
      </button>
    </div>
  </div>
)

const EndOfResults = ({ clearFilters }) => (
  <div className='rounded-xl border-2 border-dashed border-gray-300 p-8 text-center dark:border-gray-600'>
    <div className='text-3xl'>🎬</div>
    <h3 className='mt-2 font-bold text-gray-900 dark:text-white'>That's all, folks!</h3>
    <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>You scrolled all the way to the bottom. I'm impressed and a little concerned.</p>
    <button type='button' onClick={clearFilters} className='mt-3 text-sm font-semibold text-cccblue hover:underline'>Clear filters and find more</button>
  </div>
)

/* ---------- page ---------- */

const SearchIndex = (props) => {
  const { initTalentKind, hideAdSidebar } = props
  const [endOfResults, setEndOfResults] = useState(false)
  const urlParams = new window.URLSearchParams(window.location.search)
  const { getRequest, createQueryString } = useQuery()
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState({})
  const [fetched, setFetched] = useState(false)
  const [payment, setPayment] = useState(urlParams.get('payment') || 'all')
  const [classification, setClassification] = useState(urlParams.get('classification') || 'all')
  const [order, setOrder] = useState(urlParams.get('order_by') || 'magic')
  const [searchText, setSearchText] = useState('')
  const dropdownRef = useRef(false)
  const { dropdownOpen, openDropdown, setDropdownOpen } = useDropdown(dropdownRef)
  const [page, setPage] = useState(1)

  const [resultState, setResultState] = useReducer(
    (resultState, newResultState) => (
      { ...resultState, ...newResultState }), { results: [], favorites: [], dismissals: [] }
    )
  const { results, favorites, dismissals } = resultState

  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    arrayifyParams(urlParams.get('search'), { ...cleanFilters(), talentKind: initTalentKind || 'all' })
  )
  const { talentKind, contains, notContains, containsMode } = state

  // Everything applied beyond talent kind, flattened for the chip row.
  const activeChips = [
    ...contains.map(v => ({ key: 'contains', kind: 'contains', text: v, value: v })),
    ...notContains.map(v => ({ key: 'notContains', kind: 'not-contains', text: `not: ${v}`, value: v })),
    ...FILTER_KINDS.flatMap(k => state[k.key].map(v => ({ key: k.key, kind: k.kind, text: v, value: v })))
  ]
  const totalAppliedFilters = activeChips.length
  // A shared link with filters should land with the panel open.
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(totalAppliedFilters > 0)
  const [openKind, setOpenKind] = useState(null)
  const [showAllKinds, setShowAllKinds] = useState(RARE_KINDS.includes(talentKind))
  const [notText, setNotText] = useState('')

  // Active chips grouped by filter so each group can carry its All/Any toggle.
  const chipGroups = [
    { key: 'contains', label: 'Contains', mode: 'containsMode', noun: 'words', values: contains },
    { key: 'notContains', label: 'Excluding', values: notContains },
    ...FILTER_KINDS.map(k => ({ key: k.key, label: k.label, mode: `${k.key}Mode`, noun: k.noun, values: state[k.key] }))
  ].filter(g => g.values.length > 0)
  const undoLast = totalAppliedFilters > 0 ? () => { const last = activeChips[activeChips.length - 1]; removeFrom(last.key, last.value) } : null

  const clearFilters = () => {
    setPage(1)
    setOrder('magic')
    setPayment('all')
    setClassification('all')
    setEndOfResults(false)
    setState(cleanFilters())
  }

  const addTo = (key, value) => {
    const v = (value || '').trim()
    if (!v || state[key].includes(v)) { return }
    setState({ [key]: [...state[key], v] })
  }
  const removeFrom = (key, value) => setState({ [key]: state[key].filter(item => item !== value) })

  const addLabel = (label, labelKind) => addTo(KIND_TO_KEY[labelKind], label)
  const submitSearch = (e) => { e.preventDefault(); addTo('contains', searchText); setSearchText('') }
  const submitNot = (e) => { e.preventDefault(); addTo('notContains', notText); setNotText('') }

  useDebouncedEffect(() => { fetchData({ newPage: true }) }, 200, [state])
  useDebouncedEffect(() => { fetchData({ newPage: true }) }, 300, [order, payment, classification])
  useDebouncedEffect(() => { fetchData({}) }, 200, [page])
  useEffect(() => {
    fetchData({ updateHistory: false })
  }, [])

  // Ezoic's dynamic-content pattern. Infinite scroll appends result rows (and
  // therefore new in-feed slots) without a route change, so App.jsx's
  // route-level hook never sees them. showAds('.ezoicad') skips slots that
  // already hold an ad and fills only the new ones, so it's safe to re-run on
  // every append. Debounced so rapid filter typing doesn't spam the auction —
  // Ezoic publishes no explicit throttle, so we only ask once results settle.
  useDebouncedEffect(() => {
    if (results.length === 0) { return }
    requestEzoicAds()
  }, 300, [results.length])

  const loadMore = () => {
    // Re-entry guard: the sentinel can report "visible" several times in quick
    // succession (scroll jitter, list re-animating). Without the `loading`
    // check each report would bump the page and fire another search before the
    // previous one returned, multiplying OpenSearch traffic.
    if (loading || endOfResults) { return }
    setLoading(true)
    setPage(parseInt(page, 10) + 1)
  }

  const fetchData = ({ newPage = false, updateHistory = true }) => {
    setDropdownOpen(false)
    let search = `is:${talentKind}`
    // Emit `<prefix>-mode:or` before each OR-mode category. Skip when the
    // category has < 2 chips — single-term groups behave identically under
    // AND or OR, and the empty token keeps the URL tidy for shared links.
    const appendCategory = (prefix, items, mode) => {
      if (mode === 'or' && items.length > 1) {
        search = `${search}|${prefix}-mode:or`
      }
      for (const item of items) { search = `${search}|${prefix}:${item}` }
    }
    for (const k of FILTER_KINDS) { appendCategory(k.kind, state[k.key], state[`${k.key}Mode`]) }
    appendCategory('co', contains, containsMode)
    for (const item of notContains) { search = `${search}|nc:${item}` }

    setLoading(true)
    if (newPage) { setEndOfResults(false) }
    const data = {
      page: newPage ? 1 : page,
      payment: payment,
      classification: classification,
      order_by: order,
      search: search
    }
    if (updateHistory) { history.push(`/?${createQueryString(data)}`) }
    getRequest('/api/v3/omnisearch/projects_kick', data, (err, jsonData) => {
      if (err) { /* handled in hook */ return }
      setLoading(false)
      setFetched(true)
      // Scrolling past the last page returns empty meta; adopting that wiped
      // the header count. Keep whatever count we already have in that case.
      if (jsonData.meta?.totalEntries != null || newPage) { setMeta(jsonData.meta || {}) }

      if (newPage) {
        setResultState({
          results: jsonData.results,
          favorites: jsonData.favoriteIds,
          dismissals: jsonData.dismissalIds
        })
        return
      }
      if (jsonData.results.length > 0) {
        // Dedupe by projectId. The backend paginates by roles (100 per
        // page) but the response is grouped by project, so a project
        // whose roles span a page boundary appears on both pages. Without
        // this filter, React renders duplicate keys and the user sees
        // the same project twice in the infinite-scroll list.
        const seen = new Set(results.map(r => r.projectId))
        const fresh = jsonData.results.filter(r => !seen.has(r.projectId))
        if (fresh.length > 0) {
          setResultState({
            results: [...results, ...fresh],
            dismissals: [...dismissals, ...jsonData.dismissalIds],
            favorites: [...favorites, ...jsonData.favoriteIds]
          })
        } else if (jsonData.meta && jsonData.meta.nextPage) {
          // Every project on this page was already shown — skip ahead
          // so the user doesn't perceive a stall in the infinite scroll.
          setPage(parseInt(jsonData.meta.nextPage, 10))
        } else {
          setEndOfResults(true)
        }
      } else {
        setEndOfResults(true)
      }
    })
  }

  // totalEntries is null when the backend's cached page has no count (page
  // overflow, or a countless page), so never print the raw number blindly.
  const total = meta?.totalEntries
  const countCopy = total == null
    ? 'Open roles waiting on you'
    : total == '10000' ? 'More than 10,000 open roles waiting on you' : `${total} open ${total == 1 ? 'role' : 'roles'} waiting on you`

  return <>
    <div className='xl:flex xl:gap-x-2'>
    <div id='searchresults' className='w-full max-w-6xl space-y-3'>

      {/* Search strip */}
      <div className='rounded-2xl bg-white p-3 shadow-sm dark:bg-gray-800 sm:p-4'>
        <div className='flex flex-wrap items-center gap-2'>
          <form onSubmit={submitSearch} className='relative min-w-[12rem] flex-1'>
            <SearchIcon className='pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
            <input type='search' value={searchText} onChange={(e) => setSearchText(e.target.value)}
              placeholder='Search roles & projects. Enter to add a word.'
              aria-label='Search roles and projects'
              className='!rounded-full !pl-10 !pr-4 !py-2.5 w-full' />
          </form>
          <div className='relative'>
            <button type='button' onClick={openDropdown} aria-haspopup='listbox' aria-expanded={dropdownOpen}
              className='inline-flex items-center gap-x-1 rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-cccblue dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600'>
              <SortAscendingIcon className='h-4 w-4' />
              <span className='hidden sm:inline'>{orderMap[order]}</span>
              <ChevronDownIcon className='h-4 w-4' />
            </button>
            { dropdownOpen && <div ref={dropdownRef} role='listbox' tabIndex='-1' className='absolute right-0 top-full z-30 mt-1 w-44 overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800'>
              { Object.keys(orderMap).map((key) => (
                <button key={key} type='button' role='option' aria-selected={order === key} onClick={() => setOrder(key)}
                  className={classNames('flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700',
                    order === key ? 'font-semibold text-cccblue' : 'text-gray-700 dark:text-gray-100')}>
                  {orderMap[key]}
                  { order === key && <CheckIcon className='h-4 w-4' /> }
                </button>
              ))}
            </div> }
          </div>
          <button type='button' onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} aria-expanded={showAdvancedFilters}
            className={classNames('inline-flex items-center gap-x-1 rounded-full px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-cccblue',
              showAdvancedFilters || totalAppliedFilters > 0 ? 'bg-cccblue text-white hover:bg-cccblue-alt' : 'bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600')}>
            <AdjustmentsIcon className='h-4 w-4' />
            Filters
            { totalAppliedFilters > 0 && <span className='ml-0.5 rounded-full bg-white px-1.5 text-[11px] text-cccblue'>{totalAppliedFilters}</span> }
          </button>
        </div>

        {/* Talent kind chips */}
        <div className='-mx-3 mt-2 flex gap-2 overflow-x-auto px-3 py-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0' style={{ scrollbarWidth: 'thin' }}>
          <Chip active={talentKind === 'all'} onClick={() => setState({ talentKind: 'all' })}>
            <ViewGridIcon className='h-4 w-4' /> Everything
          </Chip>
          { Object.keys(talentKindHash).filter((key) => showAllKinds || !RARE_KINDS.includes(key)).map((key) => (
            <Chip key={`role${key}`} active={talentKind === key} onClick={() => setState({ talentKind: key })}>
              <SVGIcon name={talentKindHash[key].image} className='h-5 w-5' />
              {talentKindHash[key].text}
            </Chip>
          ))}
          { !showAllKinds && <Chip onClick={() => setShowAllKinds(true)} title='Show every kind of role'>
            More <ChevronDownIcon className='h-4 w-4' />
          </Chip> }
        </div>

        <div className='mt-3 flex flex-wrap items-center gap-2'>
          <Segmented label='Pay' value={payment} onChange={setPayment} options={PAYMENT_OPTIONS} />
          <Segmented label='Project type' value={classification} onChange={setClassification} options={CLASSIFICATION_OPTIONS} />
        </div>

        {/* Active filter chips */}
        { totalAppliedFilters > 0 && <div className='mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-100 pt-3 dark:border-gray-700'>
          { chipGroups.map((g) => (
            <div key={g.key} className='flex flex-wrap items-center gap-1.5'>
              <span className='text-xs text-gray-500 dark:text-gray-400'>{g.label}</span>
              { g.values.map((v) => <Chip key={`${g.key}${v}`} active onRemove={() => removeFrom(g.key, v)}>{v}</Chip>) }
              { g.mode && g.values.length > 1 && <AndOrToggle value={state[g.mode]} onChange={(m) => setState({ [g.mode]: m })} noun={g.noun} /> }
            </div>
          ))}
          <button type='button' onClick={clearFilters} className='text-xs font-semibold text-gray-500 hover:text-cccblue dark:text-gray-400'>Clear all</button>
          <span className='ml-auto'><SavedSearchProjectModal /></span>
        </div> }

        {/* Filter panel */}
        { showAdvancedFilters && <div className='mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-700'>
          { FILTER_KINDS.map((k) => (
            <FilterPill key={k.key} label={k.label} count={state[k.key].length} open={openKind === k.key}
              onToggle={() => setOpenKind(openKind === k.key ? null : k.key)} onClose={() => setOpenKind(null)}>
              <TagAutocomplete kind={k.kind} addLabel={addLabel} placeholder={k.placeholder} />
            </FilterPill>
          ))}
          <FilterPill label='Exclude words' count={notContains.length} open={openKind === 'notContains'}
            onToggle={() => setOpenKind(openKind === 'notContains' ? null : 'notContains')} onClose={() => setOpenKind(null)}>
            <form onSubmit={submitNot} className='flex gap-x-1'>
              <input type='text' value={notText} onChange={(e) => setNotText(e.target.value)} placeholder='Gacha' aria-label='Exclude words' />
              <button type='submit' className='rounded-md bg-gray-100 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-100'>Add</button>
            </form>
          </FilterPill>
        </div> }
      </div>

      {/* Results header */}
      <div className='flex items-center justify-between px-1 text-sm text-gray-600 dark:text-gray-300'>
        <span>{ fetched ? countCopy : 'Digging through the pile...' }</span>
        { totalAppliedFilters === 0 && <SavedSearchProjectModal /> }
      </div>

      { loading && <div className='mt-5'><Loading /></div> }
      { !loading && fetched && results.length === 0 && <EmptyState clearFilters={clearFilters} undoLast={undoLast} /> }

      { results.length > 0 && <motion.section
        key={results[0]?.projectId}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className={classNames('flex flex-col space-y-3 transition-opacity', loading && 'opacity-50')}
      >
        { results.flatMap((result, idx) => {
          const position = idx + 1
          const nodes = [
            <ProjectResultRow key={`presult${result.projectId}`} result={result} lastElementInList={idx + 1 === results.length} loadMore={loadMore} favorites={favorites} dismissals={dismissals} endOfResults={endOfResults} />
          ]
          // Ad slots are keyed by POSITION, not by the result they follow, so
          // a filter change that returns a different project list reuses the
          // same ad DOM nodes instead of tearing down already-filled ads.
          if (position >= IN_FEED_FIRST_AFTER && (position - IN_FEED_FIRST_AFTER) % IN_FEED_EVERY === 0) {
            nodes.push(<InFeedAd key={`infeed-slot-${position}`} />)
          }
          return nodes
        })}
        { loading && <Loading /> }
        { endOfResults && <EndOfResults clearFilters={clearFilters} /> }
      </motion.section> }
      {/* Below the results on purpose: opening the page with a stack of promo
          blocks before the first result blew past the Better Ads mobile
          density line, and the InFeedAd slots above carry in-results density
          now. */}
      <MobileAdBundle ezoicAdId={104} />
    </div>
    { !hideAdSidebar && <SidebarAdBundle ezoicAdId={105} /> }
    </div>
  </>
}

export default SearchIndex

SearchIndex.propTypes = { }
